import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["notification", "form"]
  static values = {
    hasUnsavedChanges: Boolean,
    minHeight: { type: Number, default: 100 }, // minimum height in pixels
    maxHeight: { type: Number, default: 500 }  // maximum height in pixels
  }

  connect() {
    this.hasUnsavedChangesValue = false
    window.addEventListener('beforeunload', this.handleBeforeUnload)

    // Listen for Trix editor changes
    this.element.addEventListener("trix-change", this.handleChange.bind(this))
    this.element.addEventListener("trix-attachment-add", this.handleChange.bind(this))
    this.element.addEventListener("trix-attachment-remove", this.handleChange.bind(this))
    this.element.addEventListener("trix-initialize", this.setupAutoResize.bind(this))
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    this.element.removeEventListener("trix-change", this.handleChange.bind(this))
    this.element.removeEventListener("trix-attachment-add", this.handleChange.bind(this))
    this.element.removeEventListener("trix-attachment-remove", this.handleChange.bind(this))
    this.element.removeEventListener("trix-initialize", this.setupAutoResize.bind(this))

    // Clean up resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  setupAutoResize() {
    const trixEditor = this.element.querySelector('trix-editor')
    if (!trixEditor) return

    // Set initial height
    this.adjustEditorHeight(trixEditor)

    // Create a ResizeObserver to watch for content changes
    this.resizeObserver = new ResizeObserver(() => {
      this.adjustEditorHeight(trixEditor)
    })

    // Start observing the editor itself
    this.resizeObserver.observe(trixEditor)

    // Also handle changes through the Trix change event
    trixEditor.addEventListener('trix-change', () => {
      this.adjustEditorHeight(trixEditor)
    })
  }

  adjustEditorHeight(editor) {
    requestAnimationFrame(() => {
      // Reset height to allow proper scrollHeight calculation
      editor.style.height = 'auto'

      // Get the scroll height (full content height)
      const scrollHeight = editor.scrollHeight

      // Calculate new height within min/max bounds
      const newHeight = Math.max(
        this.minHeightValue,
        Math.min(scrollHeight, this.maxHeightValue)
      )

      // Apply the new height
      editor.style.height = newHeight + 'px'

      // Add overflow if content exceeds max height
      editor.style.overflowY = scrollHeight > this.maxHeightValue ? 'auto' : 'hidden'
    })
  }

  handleBeforeUnload = (event) => {
    if (this.hasUnsavedChangesValue) {
      event.preventDefault()
      event.returnValue = ''
    }
  }

  handleChange(event) {
    // Clear any existing timeout
    clearTimeout(this.timeout)
    this.hasUnsavedChangesValue = true

    // Set a new timeout to save after 500ms of no changes
    this.timeout = setTimeout(() => this.submit(), 500)
  }

  submit() {
    const form = this.formTarget
    const data = new FormData(form)

    // Add the CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(form.action, {
      method: form.method,
      body: data,
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': csrfToken
      },
      credentials: 'same-origin'
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.json()
      })
      .then(data => {
        this.hasUnsavedChangesValue = false
        this.showNotification('Poznámky byly úspěšně uloženy')
      })
      .catch(error => {
        console.error('Error:', error)
        this.showNotification('Nelze uložit poznámky', true)
      })
  }

  showNotification(message, isError = false) {
    if (this.hasNotificationTarget) {
      const notification = this.notificationTarget
      notification.textContent = message

      // Remove all possible classes
      notification.classList.remove(
        'hidden',
        'bg-green-100',
        'text-green-700',
        'bg-red-100',
        'text-red-700',
        'animate-fade-out'
      )

      // Add appropriate status classes
      if (isError) {
        notification.classList.add('bg-red-100', 'text-red-700')
      } else {
        notification.classList.add('bg-green-100', 'text-green-700')
      }

      // Add styling and animation classes
      notification.classList.add('rounded-md', 'p-2', 'animate-fade-in')

      // Hide notification after 3 seconds with fade out
      setTimeout(() => {
        notification.classList.add('animate-fade-out')
        setTimeout(() => {
          notification.classList.add('hidden')
          notification.classList.remove('animate-fade-out')
        }, 300)
      }, 3000)
    }
  }
} 